import { useEffect, useState, useCallback, useRef } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { updateRestorationState, addCleanUpId } from 'actions/backRestoration';
import { useHistory, useLocation } from 'react-router-dom';
import { VideoCtvRedux } from '../store-types';

type RestorationState = {
  isAnimating: boolean;
  top: number;
  startIndex: number;
  endIndex: number;
  isFocusOnRows: boolean;
} | null;

export const useRestoration = (restorationId: string, shouldCleanUp = false) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { state: locationState } = useLocation<{ restore?: boolean }>();
  const shouldProvideRestoration = useRef(
    history.action === 'POP' || !!locationState?.restore,
  );

  const backRestoration = useSelector(
    (reduxState: VideoCtvRedux) => reduxState.backRestoration.restorationState[restorationId],
  );

  const restorationState = shouldProvideRestoration.current
    ? backRestoration
    : null;

  const [tempRestorationState, setTempRestorationState] = useState(
    restorationState,
  );

  const latestRestorationRef = useRef(null);

  useEffect(() => {
    if (shouldCleanUp) {
      dispatch(addCleanUpId(restorationId));
    }
  }, [dispatch, restorationId, shouldCleanUp]);

  useEffect(() => {
    latestRestorationRef.current = tempRestorationState;
  }, [tempRestorationState]);

  useEffect(() => {
    return () => {
      if (restorationId && latestRestorationRef.current) {
        dispatch(
          updateRestorationState(restorationId, latestRestorationRef.current),
        );
      }
    };
  }, [restorationId, dispatch]);

  const setRestorationState = useCallback((state: RestorationState) => {
    setTempRestorationState(state);
    latestRestorationRef.current = state;
  }, []);

  return {
    setRestorationState,
    restorationState,
  };
};